import { mat2d, vec2, type ReadonlyVec2 } from 'gl-matrix';

import { mod } from '../numbers';

// some extended functionality to go with gl-matrix's mat2d
// please follow the api patterns established by gl-matrix

const ROTATION_MATRICES = [
  mat2d.create(),
  mat2d.fromValues(0, 1, -1, 0, 0, 0),
  mat2d.fromValues(-1, 0, 0, -1, 0, 0),
  mat2d.fromValues(0, -1, 1, 0, 0, 0),
];

/**
 * Rotates a mat2d by 90 degrees.
 * This is a drop-in replacement for mat2d.rotate(m, m, Math.PI * 0.5)
 * but is more accurate as it doesn't suffer from floating point imprecision
 *
 * @param out - the receiving matrix
 * @param a - the matrix to rotate
 * @returns out
 */
const rotate90 = (out: mat2d, a: mat2d): mat2d => {
  return mat2d.multiply(out, a, ROTATION_MATRICES[1]);
};

/**
 * Rotates a mat2d by 180 degrees.
 * This is a drop-in replacement for mat2d.rotate(m, m, Math.PI * 0.5)
 * but is more accurate as it doesn't suffer from floating point imprecision
 *
 * @param out - the receiving matrix
 * @param a - the matrix to rotate
 * @returns out
 */
const rotate180 = (out: mat2d, a: mat2d): mat2d => {
  return mat2d.multiply(out, a, ROTATION_MATRICES[2]);
};

/**
 * Rotates a mat2d by 270 degrees.
 * This is a drop-in replacement for mat2d.rotate(m, m, Math.PI * 0.5)
 * but is more accurate as it doesn't suffer from floating point imprecision
 *
 * @param out - the receiving matrix
 * @param a - the matrix to rotate
 * @returns out
 */
const rotate270 = (out: mat2d, a: mat2d): mat2d => {
  return mat2d.multiply(out, a, ROTATION_MATRICES[3]);
};

/**
 * Rotates a mat2d by an amount of quarter turns
 * Equivalent to calling rotate90 an amount of times in succession
 * This is a drop-in replacement for mat2d.rotate(m, m, amount * Math.PI * 0.5)
 * but is more accurate as it doesn't suffer from floating point imprecision
 *
 * @param out - the receiving matrix
 * @param a - the matrix to rotate
 * @param amount - number of quarter turns to perform
 * @returns out
 */
const rotateByQuarterTurns = (out: mat2d, a: mat2d, amount: number): mat2d => {
  const rotationMatrix = ROTATION_MATRICES[mod(amount, 4)];
  return mat2d.multiply(out, a, rotationMatrix);
};

const rotateAroundNegativeOffsetFlyweight = vec2.create();

/**
 * Rotates a mat2d around a point
 *
 * @param out - the receiving matrix
 * @param matrix - the matrix to rotate
 * @param rotation - the amount of rotation to apply in radians clockwise
 * @param origin - the point to rotate around
 * @returns out
 */
const rotateAround = (
  out: mat2d,
  m: mat2d,
  rotation: number,
  origin: ReadonlyVec2,
): mat2d => {
  vec2.negate(rotateAroundNegativeOffsetFlyweight, origin);
  mat2d.translate(out, m, origin);
  mat2d.rotate(out, out, rotation);
  mat2d.translate(out, out, rotateAroundNegativeOffsetFlyweight);
  return out;
};

export const mat2dextended = {
  rotate90,
  rotate180,
  rotate270,
  rotateByQuarterTurns,
  rotateAround,
};
